import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@illegally-parked/routes/Routes';
import { MultipleTextInputTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { useParkingStore } from '@illegally-parked/store/store';
import { vehicleDetailsSchema } from '../../validations/vehicleDetails';
import { parseZodErrors } from '@utils';
import { ZodError } from 'zod';
import { REGISTRATION_NUMBER_CHARACTER_LIMIT } from '@shared/constants';

const vehicleDetailsQuestionData = {
  title: 'Details of the vehicle',
  textInputList: [
    {
      id: 'registrationNumber',
      labelText: 'Registration number',
      description:
        'Without it our officers will not be able to action your report.',
      errorPath: 'registrationNumber',
      maxLength: REGISTRATION_NUMBER_CHARACTER_LIMIT,
    },
    {
      id: 'make',
      labelText: 'Make (optional)',
      errorPath: 'make',
      maxLength: 100,
    },
    {
      id: 'model',
      labelText: 'Model (optional)',
      errorPath: 'model',
      maxLength: 100,
    },
    {
      id: 'colour',
      labelText: 'Colour (optional)',
      errorPath: 'colour',
      maxLength: 100,
    },
  ],
};

export const VehicleDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const { vehicleDetails, updateVehicleDetails } = useParkingStore();
  const textInputList = vehicleDetailsQuestionData.textInputList.map(
    (textInput) => {
      return {
        ...textInput,
        initialTextValue:
          vehicleDetails[textInput.id as keyof typeof vehicleDetails] || '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setErrorMessages([]);
          updateVehicleDetails({
            ...vehicleDetails,
            [textInput.id]: e.target.value,
          });
        },
        errorMessages,
      };
    },
  );

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.ISSUE_TYPE);
    },
  };

  const buttonNext = {
    onClick: () => {
      try {
        const validatedData = vehicleDetailsSchema.parse({
          registrationNumber: vehicleDetails.registrationNumber || '',
          make: vehicleDetails.make || '',
          model: vehicleDetails.model || '',
          colour: vehicleDetails.colour || '',
        });
        updateVehicleDetails(validatedData);
        navigate(RoutesList.UPLOAD_PHOTO);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error) => ({
            message: error.message,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <MultipleTextInputTemplate
      textInputList={textInputList}
      title={vehicleDetailsQuestionData.title}
      isLabelTextTitle={true}
      buttonPrevious={buttonPrevious}
      buttonNext={buttonNext}
      errorMessages={errorMessages}
    />
  );
};
